// Record header "⋯" menu — window.RecordMenu.
//
// A small button that opens a short list of actions for the record. The list
// portals to document.body (zIndex 400, above dialogs) so a scrolling header
// or a clipped column can't cut it off. Keyboard: ↓/↑ (and Home/End) move,
// Enter or Space picks, Escape closes (window.useEscapeKey, so a nested
// overlay closes one layer at a time), Tab and a click outside close it.
// The list and the button handle Escape themselves too: a Section action slot
// stops keydown (so a key never folds the card), and React carries the
// portaled list's keydown through it, so the document listener never hears it.
//
// items: [{ key, label, onSelect, disabled }] — falsy entries are skipped, so
// callers can write `cond && { … }`; `{ divider: true }` draws a rule between
// groups (leading, trailing and doubled rules collapse; the arrow keys skip
// them); `disabled: true` draws the item dimmed and inert (aria-disabled; a
// click or Enter does nothing and keeps the list open). Renders nothing when
// no item is left.

const RECORD_MENU_W = 220;

// What the list draws, in order: falsy entries out, dividers collapsed.
const recordMenuRows = (items) => {
  const out = [];
  (items || []).forEach((it) => {
    if (!it) return;
    if (it.divider) { if (out.length && !out[out.length - 1].divider) out.push(it); return; }
    out.push(it);
  });
  while (out.length && out[out.length - 1].divider) out.pop();
  return out;
};

// The list's height before it is drawn: 29px a row, 9px a rule, 10px of
// padding and border. Placement re-measures once the list is there.
const recordMenuHeight = (rows) => rows.reduce((h, it) => h + (it.divider ? 9 : 29), 10);

// Where the list goes: under the button, right-aligned to it, kept inside the
// viewport; above the button when it would run past the bottom (a card's ⋯
// low on the page — any scroll closes the list, so it can't be scrolled to).
const recordMenuPlace = (r, h, vw, vh) => {
  const left = Math.max(8, Math.min(r.right - RECORD_MENU_W, vw - RECORD_MENU_W - 8));
  const below = r.bottom + 4;
  return { top: below + h > vh - 8 ? Math.max(8, r.top - 4 - h) : below, left };
};

const RecordMenu = ({ items, title = 'More actions', size = 'xs' }) => {
  const [open, setOpen] = React.useState(false);
  const [active, setActive] = React.useState(0);
  const [pos, setPos] = React.useState(null);
  const anchorRef = React.useRef(null);
  const listRef = React.useRef(null);
  const rows = recordMenuRows(items);
  const shown = rows.filter((it) => !it.divider);   // the pickable items: `active` indexes these
  // Phone: the list is a bottom sheet (window.PhoneSheet: 52px rows, its own
  // Escape and backdrop), so none of the placement / outside-click code runs there.
  const phone = !!(window.AtlasDevice && window.AtlasDevice.phone);

  const close = React.useCallback((refocus) => {
    setOpen(false);
    setPos(null);
    if (refocus && anchorRef.current) {
      const btn = anchorRef.current.querySelector('button');
      if (btn) { try { btn.focus(); } catch (_) { /* detached */ } }
    }
  }, []);
  window.useEscapeKey(open && !phone, () => close(true));

  // Placed from the estimate first, then once more from the drawn list's real
  // height (a second pass that lands on the same spot sets nothing).
  React.useLayoutEffect(() => {
    if (!open || phone || !anchorRef.current) return;
    const r = anchorRef.current.getBoundingClientRect();
    const h = (listRef.current && listRef.current.offsetHeight) || recordMenuHeight(rows);
    const next = recordMenuPlace(r, h, window.innerWidth, window.innerHeight);
    if (!pos || pos.top !== next.top || pos.left !== next.left) setPos(next);
  }, [open, pos]);

  // Focus the list once it is placed, so the arrow keys land on it.
  React.useEffect(() => {
    if (open && pos && listRef.current) {
      try { listRef.current.focus({ preventScroll: true }); } catch (_) { listRef.current.focus(); }
    }
  }, [open, pos]);

  // A click outside, a page scroll or a resize closes it (the list is
  // fixed-positioned, so it would otherwise float away from the button).
  React.useEffect(() => {
    if (!open || phone) return undefined;
    const inside = (t) => (listRef.current && listRef.current.contains(t))
      || (anchorRef.current && anchorRef.current.contains(t));
    const onDown = (e) => { if (!inside(e.target)) close(false); };
    const onScroll = (e) => { if (!(listRef.current && listRef.current.contains(e.target))) close(false); };
    const onResize = () => close(false);
    document.addEventListener('mousedown', onDown);
    document.addEventListener('touchstart', onDown);
    document.addEventListener('scroll', onScroll, true);
    window.addEventListener('resize', onResize);
    return () => {
      document.removeEventListener('mousedown', onDown);
      document.removeEventListener('touchstart', onDown);
      document.removeEventListener('scroll', onScroll, true);
      window.removeEventListener('resize', onResize);
    };
  }, [open, close]);

  // The items can all drop out while the list is open (a panel just found
  // its record) — close, so no invisible list keeps Escape and the
  // outside-click listeners.
  React.useEffect(() => {
    if (open && !shown.length) close(false);
  }, [open, shown.length, close]);

  if (!shown.length) return null;

  const toggle = () => {
    if (open) { close(false); return; }
    setActive(0);
    setOpen(true);
  };
  const pick = (it) => {
    if (it && it.disabled) return;
    close(true);
    if (it && it.onSelect) it.onSelect();
  };
  const onListKey = (e) => {
    const n = shown.length;
    if (e.key === 'ArrowDown') { e.preventDefault(); setActive((i) => (i + 1) % n); }
    else if (e.key === 'ArrowUp') { e.preventDefault(); setActive((i) => (i - 1 + n) % n); }
    else if (e.key === 'Home') { e.preventDefault(); setActive(0); }
    else if (e.key === 'End') { e.preventDefault(); setActive(n - 1); }
    else if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); pick(shown[Math.min(active, n - 1)]); }
    else if (e.key === 'Escape') { e.preventDefault(); e.stopPropagation(); close(true); }
    else if (e.key === 'Tab') close(false);
  };
  // ↓ on the closed button opens the list too; Escape on it closes an open one.
  const onAnchorKey = (e) => {
    if (!open && e.key === 'ArrowDown') { e.preventDefault(); setActive(0); setOpen(true); }
    else if (open && e.key === 'Escape') { e.preventDefault(); e.stopPropagation(); close(true); }
  };

  return (
    <span ref={anchorRef} onKeyDown={onAnchorKey} style={{ display: 'inline-flex' }}>
      <Btn size={size} variant="outline" active={open} title={title} onClick={toggle}>
        <Icon.More size={12} />
      </Btn>
      {phone && window.PhoneSheet && window.PhoneActionList && (
        <window.PhoneSheet open={open} onClose={() => close(true)} title={title}>
          <window.PhoneActionList items={shown.map((it) => ({ key: it.key || it.label, label: it.label, disabled: !!it.disabled, onClick: () => pick(it) }))} />
        </window.PhoneSheet>
      )}
      {open && pos && !phone && ReactDOM.createPortal(
        <div ref={listRef} role="menu" aria-label={title} tabIndex={-1} onKeyDown={onListKey}
          style={{
            position: 'fixed', top: pos.top, left: pos.left, width: RECORD_MENU_W, zIndex: 400,
            background: 'var(--surface)', border: '1px solid var(--border)', borderRadius: 8,
            boxShadow: 'var(--shadow-lg)', overflow: 'hidden', padding: '4px 0', outline: 'none',
          }}>
          {rows.map((it, r) => {
            if (it.divider) return <div key={`rule-${r}`} role="separator" style={{ height: 1, margin: '4px 0', background: 'var(--border)' }} />;
            const i = shown.indexOf(it);
            return (
              <button key={it.key || it.label} type="button" role="menuitem" tabIndex={-1}
                aria-disabled={it.disabled ? 'true' : undefined}
                onClick={() => pick(it)}
                onMouseEnter={() => setActive(i)}
                style={{
                  display: 'block', width: '100%', padding: '7px 12px', textAlign: 'left', border: 'none',
                  background: i === active ? 'var(--surface-hover)' : 'var(--surface)', cursor: it.disabled ? 'default' : 'pointer',
                  fontSize: 12.5, color: it.disabled ? 'var(--text-4)' : 'var(--text)', fontFamily: 'inherit',
                }}>
                {it.label}
              </button>
            );
          })}
        </div>,
        document.body,
      )}
    </span>
  );
};

window.RecordMenu = RecordMenu;
window.recordMenuRows = recordMenuRows;
window.recordMenuPlace = recordMenuPlace;
window.recordMenuHeight = recordMenuHeight;
